import { v4 as uuidv4 } from "uuid";
import {
  ITeamsTeamcenterLoggerSeverityLevel,
  ITeamsTeamcenterLoggerSeverityLevelType,
  getTeamsTeamcenterLoggerSeverityLevelEnum,
} from "./ITeamsTeamcenterLoggerSeverityLevel";
import { ITeamsTeamcenterLogger, LogContext } from "./ITeamsTeamcenterLogger";
import { ITeamsTeamcenterLoggerConfiguration } from "./ITeamsTeamcenterLoggerConfiguration";
import { TeamsTeamcenterLoggerConfiguration } from "./TeamsTeamcenterLoggerConfiguration";
import {
  ApplicationInsights,
  SeverityLevel,
  IEventTelemetry,
  IMetricTelemetry,
  ICustomProperties,
} from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
//import * as fs from 'fs';
//import { SeverityLevel } from 'applicationinsights/out/Declarations/Contracts';
//const path = require('path');
/// <summary>
/// Class TeamsTeamcenterLogger based on interface ITeamsTeamcenterLogger
/// </summary>
export class TeamsTeamcenterLogger implements ITeamsTeamcenterLogger {
  private _name: string = "";
  private _tenantId: string = "";
  private _telemetryClient: ApplicationInsights | null = null;
  private _activeCapability: string = "";
  private _serviceName: string = "";

  private readonly ENV_SESSION_ID: string = "SIEMENS_SESSION_ID";
  private readonly ENV_OPERATION_ID: string = "SIEMENS_OPERATION_ID";
  private readonly ENV_CORRELATION_ID: string = "LOG_CORRELATION_ID";

  private _config: ITeamsTeamcenterLoggerConfiguration;
  /// <summary>
  /// TeamsTeamcenterLogger constructor
  /// </summary>
  /// <param name="classname">Logger name</param>
  /// <param name="config">Current logger configuration ITeamsTeamcenterLoggerConfiguration</param>
  /// <returns>void</returns>
  constructor(
    classname: any | string | null,
    config: ITeamsTeamcenterLoggerConfiguration | null
  ) {
    this._name = "UNKNOWN";
    if (classname != null) {
      if (typeof classname == "string") this._name = classname;
      else this._name = (<any>classname).constructor.name;
    }
    this._tenantId = "";
    if (config == null) this._config = new TeamsTeamcenterLoggerConfiguration();
    else this._config = config;

    /* if((this.getCurrentConfig().logApplicationInsightsConnectionString != null)&&(this.getCurrentConfig().logApplicationInsightsConnectionString.trim().length>0))
            this._telemetryClient = new TelemetryClient(this.getCurrentConfig().logApplicationInsightsConnectionString);
        else
            this._telemetryClient = null;
            */
    if (
      this.getCurrentConfig().logApplicationInsightsConnectionString != null &&
      this.getCurrentConfig().logApplicationInsightsConnectionString.trim()
        .length > 0
    ) {
      var reactPlugin = new ReactPlugin();
      // Add the Click Analytics plug-in.
      /* var clickPluginInstance = new ClickAnalyticsPlugin();
               var clickPluginConfig = {
                 autoCapture: true
            }; */
      this._telemetryClient = new ApplicationInsights({
        config: {
          connectionString:
            this.getCurrentConfig().logApplicationInsightsConnectionString,
          // If you're adding the Click Analytics plug-in, delete the next line.
          extensions: [reactPlugin],
          // Add the Click Analytics plug-in.
          // extensions: [reactPlugin, clickPluginInstance],
          extensionConfig: {
            //   [reactPlugin.identifier]: { history: browserHistory }
            // Add the Click Analytics plug-in.
            // [clickPluginInstance.identifier]: clickPluginConfig
          },
          enableAutoRouteTracking: true,
          disableAjaxTracking: false,
          autoTrackPageVisitTime: true,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
        },
      });
      this._telemetryClient.loadAppInsights();
    } else this._telemetryClient = null;
  }
  /// <summary>
  /// log method per severity level
  /// </summary>
  /// <param name="message">message to be logged</param>
  /// <returns>void</returns>
  logDebug(message: string, context?: LogContext): void {
    this.log(ITeamsTeamcenterLoggerSeverityLevel.Debug, message, context);
  }
  logInformation(message: string, context?: LogContext): void {
    this.log(ITeamsTeamcenterLoggerSeverityLevel.Information, message, context);
  }
  logWarning(message: string, context?: LogContext): void {
    this.log(ITeamsTeamcenterLoggerSeverityLevel.Warning, message, context);
  }
  logTrace(message: string, context?: LogContext): void {
    this.log(ITeamsTeamcenterLoggerSeverityLevel.Trace, message, context);
  }
  logError(message: string, context?: LogContext): void {
    this.log(ITeamsTeamcenterLoggerSeverityLevel.Error, message, context);
  }
  logCritical(message: string, context?: LogContext): void {
    this.log(ITeamsTeamcenterLoggerSeverityLevel.Critical, message, context);
  }

  /// <summary>
  /// logAggregateMetric method defined in ITeamsTeamcenterLogger interface
  /// used to log an aggregate metric
  /// </summary>
  /// <param name="metricname">metric's name</param>
  /// <param name="value">metric's value</param>
  /// <param name="properties">Dictionary string, string containing the metric's properties</param>
  /// <returns>bool</returns>
  logAggregateMetric(
    metricname: string,
    value: number,
    context: LogContext,
    properties?: { [key: string]: any }
  ): void {
    var ev: IMetricTelemetry = {
      name: metricname,
      average: value,
      properties: [],
    };
    var pr: ICustomProperties = [
      { ActionId: context.actionId },
      { SessionId: context.sessionId },
      { RequestId: context.requestId },
      { TenantId: this.getTenantId() },
    ];

    if (properties != null)
      for (let key in properties) {
        if (pr != null) pr[key] = properties[key];
      }

    this._telemetryClient?.trackMetric(ev, pr);
    this._telemetryClient?.flush();
  }
  /// <summary>
  /// logMetric method defined in ITeamsTeamcenterLogger interface
  /// used to log an aggregate metric
  /// </summary>
  /// <param name="name">metric's name</param>
  /// <param name="values">Dictionarystring, double  metric's dictionary values</param>
  /// <param name="properties">Dictionary string, string containing the metric's properties</param>
  /// <returns>bool</returns>
  logMetric(
    name: string,
    values: { [key: string]: number },
    context: LogContext,
    properties?: { [key: string]: any }
  ): void {
    var ev: IEventTelemetry = { name: name, measurements: {}, properties: [] };

    if (properties != null)
      for (let key in properties) {
        if (ev.properties != null) ev.properties[key] = properties[key];
      }

    if (values != null)
      for (let key in values) {
        if (ev.measurements != null) ev.measurements[key] = values[key];
      }
    var pr: ICustomProperties = [
      { ActionId: context.actionId },
      { SessionId: context.sessionId },
      { RequestId: context.requestId },
      { TenantId: this.getTenantId() },
    ];

    this._telemetryClient?.trackEvent(ev, pr);
    this._telemetryClient?.flush();
  }
  getConsoleType(
    level: ITeamsTeamcenterLoggerSeverityLevelType
  ): "debug" | "info" | "warn" | "error" {
    if (level == ITeamsTeamcenterLoggerSeverityLevel.Debug) return "debug";
    if (level == ITeamsTeamcenterLoggerSeverityLevel.Trace) return "debug";
    if (level == ITeamsTeamcenterLoggerSeverityLevel.Information) return "info";
    if (level == ITeamsTeamcenterLoggerSeverityLevel.Warning) return "warn";
    if (level == ITeamsTeamcenterLoggerSeverityLevel.Error) return "error";
    if (level == ITeamsTeamcenterLoggerSeverityLevel.Critical) return "error";
    return "info";
  }
  private getTeamsTeamcenterLevel(
    level: ITeamsTeamcenterLoggerSeverityLevelType
  ): string {
    switch (level) {
      case ITeamsTeamcenterLoggerSeverityLevel.Debug:
        return "DEBUG";
      case ITeamsTeamcenterLoggerSeverityLevel.Information:
        return "INFO";
      case ITeamsTeamcenterLoggerSeverityLevel.Warning:
        return "WARN";
      case ITeamsTeamcenterLoggerSeverityLevel.Trace:
        return "TRACE";
      case ITeamsTeamcenterLoggerSeverityLevel.Error:
        return "ERROR";
      case ITeamsTeamcenterLoggerSeverityLevel.Critical:
        return "FATAL";
      default:
        return "UNKNOWN";
    }
  }

  getCurrentConfig(): ITeamsTeamcenterLoggerConfiguration {
    return this._config;
  }

  isEnabled(logLevel: ITeamsTeamcenterLoggerSeverityLevelType): boolean {
    let result: boolean =
      logLevel != ITeamsTeamcenterLoggerSeverityLevel.None &&
      this.getCurrentConfig().logLevel !=
        ITeamsTeamcenterLoggerSeverityLevel.None &&
      getTeamsTeamcenterLoggerSeverityLevelEnum(logLevel) >=
        getTeamsTeamcenterLoggerSeverityLevelEnum(
          this.getCurrentConfig().logLevel
        );

    return result;
  }

  private getAppInsightsLevel(
    level: ITeamsTeamcenterLoggerSeverityLevelType
  ): SeverityLevel {
    switch (level) {
      case ITeamsTeamcenterLoggerSeverityLevel.Debug:
        return SeverityLevel.Verbose;
      case ITeamsTeamcenterLoggerSeverityLevel.Information:
        return SeverityLevel.Information;
      case ITeamsTeamcenterLoggerSeverityLevel.Warning:
        return SeverityLevel.Warning;
      case ITeamsTeamcenterLoggerSeverityLevel.Trace:
        return SeverityLevel.Verbose;
      case ITeamsTeamcenterLoggerSeverityLevel.Error:
        return SeverityLevel.Error;
      case ITeamsTeamcenterLoggerSeverityLevel.Critical:
        return SeverityLevel.Critical;
      default:
        return SeverityLevel.Information;
    }
  }

  log(
    level: ITeamsTeamcenterLoggerSeverityLevelType,
    message: string,
    context?: LogContext
  ): void {
    if (this.isEnabled(level)) {
      var d = new Date();
      var sDate = d
        .toISOString()
        .replace("-", "/")
        .replace("-", "/")
        .replace("T", "-")
        .replace("Z", " UTC");
      var slevel = this.getTeamsTeamcenterLevel(level);
      let Msg: string =
        `${sDate} - ${slevel} - ${this._serviceName} - ${this._name} - ` +
        `${this._tenantId} - ${this._activeCapability} - ` +
        `${context?.requestId || ""} - ${context?.sessionId || ""} - ${context?.actionId || ""} - ` +
        `${message}`;

      if (this.getCurrentConfig().logConsole == true) {
        if (this.getConsoleType(level) == "debug") console.log(Msg);
        else console[this.getConsoleType(level)](Msg);
      }

      if (
        this.getCurrentConfig().logApplicationInsightsConnectionString !=
          null &&
        this.getCurrentConfig().logApplicationInsightsConnectionString.trim()
          .length > 0
      ) {
        this._telemetryClient?.trackTrace({
          message: Msg,
          severityLevel: this.getAppInsightsLevel(level),
        });
        this._telemetryClient?.flush();
      }
    }
  }

  /// <summary>
  /// getTenantId method defined in ITeamsTeamcenterLogger interface
  /// used to return current Tenant Id associated with the current context.
  /// </summary>
  /// <returns>string</returns>
  getTenantId(): string {
    return this._tenantId;
  }

  /// <summary>
  /// setTenantId LogMetric method defined in ITeamsTeamcenterLogger interface
  /// used to set the current Tenant Id
  /// </summary>
  /// <param name="tenant">Tenant id</param>
  /// <returns>void</returns>
  setTenantId(tenant: string): void {
    this._tenantId = tenant;
  }

  /// <summary>
  /// clearTenantId method defined in ITeamsTeamcenterLogger interface
  /// used to clear current Tenant Id.
  /// </summary>
  /// <returns>void</returns>
  clearTenantId(): void {
    this._tenantId = "";
  }

  /// <summary>
  /// createContextFromParentEnv method defined in ITeamsTeamcenterLogger interface
  /// used to manage correlation ids. This method reads the environment variables
  /// "SIEMENS_SESSION_ID", "SIEMENS_OPERATION_ID", "LOG_CORRELATION_ID" to initialize
  /// respectively the attributes SessionId, ActionId and RequestId.
  /// </summary>
  /// <returns>LogContext</returns>
  createContextFromParentEnv(): LogContext {
    const sessionId = process.env.SIEMENS_SESSION_ID;
    const actionId = process.env.SIEMENS_OPERATION_ID;
    const requestId = process.env.LOG_CORRELATION_ID;

    return {
      sessionId: sessionId && sessionId.length > 0 ? sessionId : "",
      actionId: actionId && actionId.length > 0 ? actionId : "",
      requestId: requestId && requestId.length > 0 ? requestId : "",
    };
  }

  /// <summary>
  /// createContext method defined in ITeamsTeamcenterLogger interface
  /// used to manage correlation ids. This method reads the input partialLogContext
  /// to initialize the attributes SessionId, ActionId and RequestId. If any of these attributes
  /// is not defined or is an empty string a new guid is generated for that attribute.
  /// </summary>
  /// <returns>LogContext</returns>
  createContext(partialLogContext: Partial<LogContext>): LogContext {
    let sessionId: string, actionId: string, requestId: string;

    if (
      partialLogContext.sessionId != null &&
      partialLogContext.sessionId.trim().length > 0
    )
      sessionId = partialLogContext.sessionId;
    else
      sessionId = uuidv4()
        .replace("-", "")
        .replace("-", "")
        .replace("-", "")
        .replace("-", "");

    if (
      partialLogContext.actionId != null &&
      partialLogContext.actionId.trim().length > 0
    )
      actionId = partialLogContext.actionId;
    else
      actionId = uuidv4()
        .replace("-", "")
        .replace("-", "")
        .replace("-", "")
        .replace("-", "");

    if (
      partialLogContext.requestId != null &&
      partialLogContext.requestId.trim().length > 0
    )
      requestId = partialLogContext.requestId;
    else
      requestId = uuidv4()
        .replace("-", "")
        .replace("-", "")
        .replace("-", "")
        .replace("-", "");

    return {
      sessionId: sessionId,
      actionId: actionId,
      requestId: requestId,
    };
  }

  /// <summary>
  /// setActiveCapability method defined in ITeamsTeamcenterLogger interface
  /// used to set the active capability name for logging purposes.
  /// </summary>
  /// <param name="capabilityName">capability name</param>
  /// <returns>void</returns>
  setActiveCapability(capabilityName: string): void {
    this._activeCapability = capabilityName;
  }

  /// <summary>
  /// setServiceName method defined in ITeamsTeamcenterLogger interface
  /// used to set the service name for logging purposes.
  /// </summary>
  /// <param name="serviceName">service name</param>
  /// <returns>void</returns>
  setServiceName(serviceName: string): void {
    this._serviceName = serviceName;
  }
}
